import { between, headingOf, turnToward, type CritterWorld, type Random } from './critter-math.js';
import { restingQuad, type QuadAction } from './quad-model.js';
import type { QuadSpecies } from './quad-species.js';

export type GrazerMode = 'graze' | 'stand' | 'walk' | 'flee';

/**
 * A grazer's wits. It crops the grass, lifting its head now and then to look about; it ambles a few metres at a time about
 * its home ground. If something comes close, a deer (skittish) freezes and watches for a moment, then bolts away at a gallop
 * and stops a good way off to look back, taking others of its kind with it (the field passes the alarm on); a sheep (placid)
 * just lifts its head and walks a few metres off, then goes back to grazing.
 */
export class GrazerBrain {
  readonly action: QuadAction = restingQuad();
  mode: GrazerMode = 'graze';
  /** Where its fear comes from while it is running, so that the herd around it can run from the same place. */
  fleeingFrom: { x: number; z: number } | null = null;
  private timer: number;
  private lift = 0; private liftIn: number;
  private goal: { x: number; z: number } | null = null;
  private noticed = 0;
  /** Frozen and watching something that has come close, before it runs (or goes back to what it was doing). */
  get watching() { return this.mode === 'stand' && this.noticed > 0; }
  /** Walking off from a hero rather than ambling about home. */
  private leaving = false;
  private fleeTime = 0;

  constructor(readonly species: QuadSpecies, public x: number, public z: number, public heading: number, private home: { x: number; z: number }, private random: Random) {
    this.action.idle = random() * 40; this.timer = between(random, 2, 9); this.liftIn = between(random, 3, 9);
  }

  update(dt: number, world: CritterWorld) {
    const a = this.action, s = this.species;
    a.idle += dt;
    const threat = this.mode === 'flee' ? null : world.threat(this.x, this.z, s.scareReach);
    if (threat && !s.skittish) this.walkOff(threat, world);
    else if (threat) {
      // Freeze and watch; run when it comes closer, or when the watching has gone on long enough.
      this.noticed += dt;
      if (this.mode !== 'stand') this.mode = 'stand';
      this.heading = turnToward(this.heading, headingOf(threat.x - this.x, threat.z - this.z), 3 * dt);
      if (this.noticed > (s.kind === 'deer' ? .8 : .35) || threat.distance < s.scareReach * .5) { this.startFlee(threat); }
    } else if (this.mode !== 'flee') this.noticed = Math.max(0, this.noticed - dt * 2);

    if (this.mode === 'flee') this.flee(dt, world);
    else if (this.mode === 'walk') this.amble(dt, world);
    else { this.timer -= dt; if (this.timer <= 0 && !threat) this.next(world); }

    // Ease toward what the current mode asks for.
    const lifted = this.lift > 0 ? (this.lift -= dt, 1) : 0;
    if (this.mode === 'graze') { this.liftIn -= dt; if (this.liftIn <= 0) { this.lift = between(this.random, .9, 1.8); this.liftIn = between(this.random, 5, 11); } }
    const want = {
      graze: this.mode === 'graze' && !lifted ? 1 : this.mode === 'walk' && s.kind === 'sheep' && !this.leaving ? .55 : 0,
      alert: this.mode === 'flee' ? .6 : threat ? (s.skittish ? 1 : .7) : lifted ? .6 : this.mode === 'stand' ? .45 : 0,
      walk: this.mode === 'walk' || this.mode === 'flee' ? 1 : 0, gait: this.mode === 'flee' ? 1 : 0,
    };
    const ease = (value: number, target: number, rate: number) => value + (target - value) * (1 - Math.exp(-dt * rate));
    a.graze = ease(a.graze, want.graze, 3.5); a.alert = ease(a.alert, want.alert, 6); a.walk = ease(a.walk, want.walk, 6); a.gait = ease(a.gait, want.gait, 5);
  }

  private startFlee(threat: { x: number; z: number }) {
    this.mode = 'flee'; this.fleeingFrom = { x: threat.x, z: threat.z }; this.fleeTime = 0; this.goal = null;
  }

  private flee(dt: number, world: CritterWorld) {
    const from = this.fleeingFrom!, s = this.species;
    this.fleeTime += dt;
    const away = headingOf(this.x - from.x, this.z - from.z), distance = Math.hypot(this.x - from.x, this.z - from.z);
    if (distance > 24 || this.fleeTime > 7) {
      // Safe: stop a good way off and watch for a while, then settle.
      this.mode = 'stand'; this.fleeingFrom = null; this.home = { x: this.x, z: this.z }; this.timer = between(this.random, 3, 6); this.noticed = 0; return;
    }
    this.move(dt, away, s.fleeSpeed, 6, world);
  }

  private amble(dt: number, world: CritterWorld) {
    const goal = this.goal;
    if (!goal || Math.hypot(goal.x - this.x, goal.z - this.z) < .6) { this.goal = null; this.arrive(); return; }
    if (!this.move(dt, headingOf(goal.x - this.x, goal.z - this.z), this.species.walkSpeed * (this.leaving ? 1.7 : 1), 2.5, world)) { this.goal = null; this.arrive(); }
  }

  /** Walks `speed` m/s toward `desired`, turning round anything in the way; false when boxed in. */
  private move(dt: number, desired: number, speed: number, turnRate: number, world: CritterWorld): boolean {
    const step = speed * this.action.walk * dt, look = step + .9;
    for (const turn of [0, .5, -.5, 1, -1, 1.6, -1.6]) {
      // Check the heading it will actually have after turning this frame, so it never walks into what it only meant to turn from.
      const heading = turnToward(this.heading, desired + turn, turnRate * dt * (turn ? 2 : 1));
      if (!world.free(this.x + Math.sin(heading) * look, this.z + Math.cos(heading) * look, .5)) continue;
      this.heading = heading;
      this.x += Math.sin(this.heading) * step; this.z += Math.cos(this.heading) * step;
      this.action.stride += step / (this.species.stride * (1 + this.action.gait * 1.6));
      return true;
    }
    return false;
  }

  /** A placid animal's answer to a hero coming close: head up, then a brisk walk a few metres directly away. */
  private walkOff(threat: { x: number; z: number }, world: CritterWorld) {
    if (this.mode === 'walk' && this.leaving) return;
    const away = headingOf(this.x - threat.x, this.z - threat.z);
    for (const turn of [0, .6, -.6, 1.2, -1.2]) {
      const goal = { x: this.x + Math.sin(away + turn) * 5, z: this.z + Math.cos(away + turn) * 5 };
      if (world.free(goal.x, goal.z, .6)) { this.goal = goal; this.mode = 'walk'; this.leaving = true; this.home = goal; return; }
    }
  }

  private arrive() { this.leaving = false; this.mode = this.random() < .65 ? 'graze' : 'stand'; this.timer = between(this.random, 4, 12); }

  private next(world: CritterWorld) {
    const roll = this.random();
    if (roll < .5) {
      for (let tries = 0; tries < 6; tries++) {
        const angle = this.random() * Math.PI * 2, reach = between(this.random, 2.5, this.species.homeRadius);
        const goal = { x: this.home.x + Math.cos(angle) * reach, z: this.home.z + Math.sin(angle) * reach };
        if (Math.hypot(goal.x - this.x, goal.z - this.z) > 2 && world.free(goal.x, goal.z, .6)) { this.goal = goal; this.mode = 'walk'; return; }
      }
    }
    this.mode = roll < .78 ? 'graze' : 'stand'; this.timer = between(this.random, 5, 14);
  }
}
